import { AfterViewInit, ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { OwlDateTimeModule, PickerMode, PickerType } from '@danielmoncada/angular-datetime-picker';

import { BAAS_DATE_TIME_PICKER_PROVIDER, BaasDateTimePickerProvider, BAR_DATE_TIME_PROVIDERS, BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { BarBaseInputCellEditor } from '../bar-base-input-cell-editor';
import { IBarDateTimeCellEditorParams } from './bar-date-time-cell-editor-params.contract';

@Component({
    selector: 'bar-date-time-cell-editor',
    templateUrl: './bar-date-time-cell-editor.html',
    imports: [MatFormFieldModule, MatInputModule, FormsModule, OwlDateTimeModule, ReactiveFormsModule, MatButtonModule, MatIconModule, BarI18nPipe],
    providers: [...BAR_DATE_TIME_PROVIDERS],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarDateTimeCellEditor extends BarBaseInputCellEditor<IBarDateTimeCellEditorParams> implements AfterViewInit, OnDestroy {
    readonly #ngZone = inject(NgZone);
    readonly #provider = inject<BaasDateTimePickerProvider>(BAAS_DATE_TIME_PICKER_PROVIDER);

    protected pickerType: PickerType;
    protected pickerMode: PickerMode;

    #observer?: MutationObserver;

    get #stopEditingWhenCellsLoseFocus() {
        return this.params.api.getGridOption('stopEditingWhenCellsLoseFocus');
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();

        if (this.#stopEditingWhenCellsLoseFocus === true) {
            this.#observer = new MutationObserver(() => {
                const owlPopup = document.querySelector('.owl-dt-popup-container');
                // here we add ag class to avoid losing focus when clicking inside owl popup
                if (owlPopup && !owlPopup.classList.contains('ag-custom-component-popup')) {
                    owlPopup.classList.add('ag-custom-component-popup');
                }
            });
            const overlayContainer = document.querySelector('.cdk-overlay-container') || document.body;
            this.#observer.observe(overlayContainer, {
                childList: true,
                subtree: true
            });
        }
    }

    ngOnDestroy(): void {
        if (this.#stopEditingWhenCellsLoseFocus === true) {
            this.#observer?.disconnect();
            // NOTE avoid ag-grid dom error in case calendar popup is open and you click outside the grid
            this.params.stopEditing(true);
        }
    }

    protected onInit(): void {
        this.pickerType = this.params.pickerType || 'calendar';
        this.pickerMode = this.params.pickerMode || 'popup';
    }

    // Override onKeyDown to add shortcut functionality like BaasLegacyDateTimePicker
    onKeyDown(event: KeyboardEvent): void {
        // First handle shortcuts
        let result: Date = undefined;
        if (this.params.shortcutHandler) {
            result = this.params.shortcutHandler(event);
        } else if (this.#provider.handleShortcut) {
            result = this.#provider.handleShortcut(event);
        }

        if (result !== undefined) {
            this.#ngZone.runOutsideAngular(() => {
                // Set the value with a timeout to ensure proper processing
                window.setTimeout(() => {
                    this.formControl.setValue(result, { emitEvent: true });
                });
            });
            return; // Don't call super if we handled the shortcut
        }

        // Call parent's onKeyDown for other key handling
        super.onKeyDown(event);
    }
}
